import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "clients-panel" }
const _hoisted_2 = {
  key: 0,
  class: "clients-panel__no-access"
}
const _hoisted_3 = { class: "clients-panel__no-access-label" }

import { i18n } from "@/i18n";
import {
	computed,
	ref,
	watch
} from "vue";
import {clientPageStore} from "@/store/client-page";
import {useRoute} from "vue-router";
import ClientsBlockPanelFilters from "@/components/main/client-block-page/ClientsBlockPanelFilters.vue";
import ClientsBlockPanelList from "@/components/main/client-block-page/ClientsBlockPanelList.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import { provideContactsContext } from "@/composables/useContactsContext";
import { useContactsAccess } from "@/composables/useContactsAccess";
import { RequestAddressContainer } from "@/composables/useAddressRequest";

type Option = {
	id: number | null,
	label: string,
	type: number,
	value: number | string
}

const ALL_SYSTEM_OPTION_VALUE = 'all-system'


export default /*@__PURE__*/_defineComponent({
  __name: 'ClientBlockPageContacts',
  setup(__props) {

const route = useRoute()
const clientStore = clientPageStore()
const { isContactsDenied, deniedText } = useContactsAccess()
const routeClientId = computed(() => {
	const parsed = parseInt(route.query.id as string)
	return Number.isNaN(parsed) ? null : parsed
})
const searchedContactId = ref<Option | null>(null)
const selectedTags = ref<TagWithFrequency[]>([])
const filters = ref({
	searchType: null as number | null,
	searchStr: '',
	inputContainer: null as RequestAddressContainer | null,
	toSkip: 0,
	count: 30
})
const appliedSearchText = ref('')

const client = computed(() => clientStore.openedClient)
const contactsTypesList = computed<Option[]>(() => {
	if (!client.value) return []

	const options: Option[] = [
		{
			id: null,
			label: String(i18n.global.t("contacts_all_system")),
			type: 0,
			value: ALL_SYSTEM_OPTION_VALUE
		}
	]

	options.push({
		id: client.value.Id,
		label: String(i18n.global.t("contacts_all_holding")),
		type: 1,
		value: 'all-holding'
	})

	options.push({
		id: client.value.Id,
		label: client.value.Name,
		type: 2,
		value: 'main-client'
	})

	client.value?.UnionClients.forEach((contact, index) => {
		options.push({
			id: contact.Id,
			label: contact.Name,
			type: 2,
			value: 4 + index
		})
	})

	return options
})

const buildRequest = () => ({
	...filters.value,
	tags: selectedTags.value.length ? selectedTags.value.map(t => t.Id) : null,
	clientId: filters.value.searchType
		? searchedContactId.value?.id as number
		: undefined,
})

const searchContacts = async (address?: RequestAddressContainer | null) => {
	if (address !== undefined) {
		filters.value.inputContainer = address
	}
	filters.value.searchType = searchedContactId.value?.type ?? null
	filters.value.toSkip = 0
	appliedSearchText.value = filters.value.searchStr
	await clientStore.getContactsForClient(buildRequest())
}

const fetchPartial = async () => {
	if (
		clientStore.clientContactsLoading
		|| clientStore.clientContactsPartialLoading
		|| !clientStore.canLoadNextContacts
	) return
	filters.value.toSkip += filters.value.count
	await clientStore.getContactsForClient(buildRequest())
}

const onChangeOption = async (option: Option) => {
	searchedContactId.value = option
	await searchContacts()
}

const showExistContacts = async (contactIds: number[], contactText: string) => {
	const allSystemOption = contactsTypesList.value.find(
		option => option.value === ALL_SYSTEM_OPTION_VALUE
	)
	if (!allSystemOption) {
		return
	}

	searchedContactId.value = allSystemOption
	filters.value.searchType = allSystemOption.type
	filters.value.toSkip = 0

	appliedSearchText.value = contactText
	await clientStore.getExistContacts(contactIds, allSystemOption.label)
}

const onSelectedTagsUpdate = (next: TagWithFrequency[]) => {
	const wasEmpty = !selectedTags.value.length
	selectedTags.value = next
	if (!next.length && !wasEmpty) {
		searchContacts()
	}
}

const dropTag = (tag: TagWithFrequency) => {
	selectedTags.value = selectedTags.value.filter(t => t.Id !== tag.Id)
	if (!selectedTags.value.length) {
		searchContacts()
	}
}

provideContactsContext({
	context: 'client',
	get attachClientId() {
		return clientStore.openedClient?.Id ?? null
	},
	refetchContacts: () => searchContacts(),
	showExistContacts,
})

const openedClientId = computed(() => client.value?.Id ?? null)

const unionClientsKey = computed(
	() => (client.value?.UnionClients ?? []).map((c: any) => c.Id).join(',')
)

const buildMainClientOption = (): Option => ({
	id: client.value?.Id || 0,
	label: client.value?.Name || '',
	type: 2,
	value: 'main-client'
})

const resetFiltersToDefault = () => {
	filters.value.toSkip = 0
	selectedTags.value = []
	searchedContactId.value = buildMainClientOption()
	filters.value.searchType = 2
}

const resyncSelectedOption = () => {
	const selected = searchedContactId.value
	if (!selected || typeof selected.value !== 'number') {
		return
	}

	const actual = contactsTypesList.value.find(
		option => typeof option.value === 'number' && option.id === selected.id
	)
	if (actual) {
		searchedContactId.value = actual
		return
	}

	searchedContactId.value = buildMainClientOption()
	filters.value.searchType = 2
}

watch([openedClientId, unionClientsKey], async (next, prev) => {
	const id = next[0]
	const prevId = prev?.[0] ?? null

	if (!id || id !== routeClientId.value) {
		return
	}

	if (id !== prevId) {
		resetFiltersToDefault()
	} else {
		resyncSelectedOption()
	}

	await searchContacts()
}, { immediate: true })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(isContactsDenied))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(AppIcon, {
            class: "clients-panel__no-access-icon",
            name: "invalid",
            width: "24px",
            color: "#979797"
          }),
          _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(deniedText)), 1)
        ]))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createVNode(ClientsBlockPanelFilters, {
            searchStr: filters.value.searchStr,
            "onUpdate:searchStr": _cache[0] || (_cache[0] = ($event: any) => ((filters.value.searchStr) = $event)),
            searchedContactId: searchedContactId.value,
            "contacts-types": contactsTypesList.value,
            "selected-tags": selectedTags.value,
            onOnSearch: searchContacts,
            onUpdateSearchedContactId: onChangeOption,
            "onUpdate:selectedTags": onSelectedTagsUpdate,
            onDropTag: dropTag
          }, null, 8, ["searchStr", "searchedContactId", "contacts-types", "selected-tags"]),
          _createVNode(ClientsBlockPanelList, {
            onOnGetPartial: fetchPartial,
            "search-text": appliedSearchText.value
          }, null, 8, ["search-text"])
        ], 64))
  ]))
}
}

})